import { AxiosResponse, AxiosRequestConfig } from 'axios';
import client from 'api/client';
import { AppDocument, Application, MilestoneApp } from '../models/response';
import { ApplicationPayload } from 'api/models/payload/ApplicationPayload';
import { MilestonePayload } from 'api/models/payload/MilestonesPayload';

export interface VisaDetails {
    country: string;
    name: string;
    start: string;
    end: string;
}

export interface ClientApplicationStatusPayload extends ApplicationPayload {
    client_status: string;
    visa_result?: 'POSITIVE' | 'NEGATIVE';
    visa?: VisaDetails;
}

export interface ReceivedVisaPayload {
    application_id: number;
    program_name: string;
    start: string;
    end: string;
}

export interface ReceivedVisaResponse extends VisaDetails {
    id: number;
    application_id: number;
    user_id: number;
}

export const documents = {
    get(
        applicationId: string,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<AppDocument[]>> {
        return client.get<AppDocument[]>(`/application/${applicationId}/documents`, customConfig);
    },
};

export const getDocumentTemplateUrl = (applicationId: string, documentId: number) => {
    return client.get(`/applications/${applicationId}/documents/${documentId}/template_uri`);
};

export const getDocumentSampleUrl = (applicationId: string, documentId: number) => {
    return client.get(`/applications/${applicationId}/documents/${documentId}/sample_uri`);
};

export const milestones = {
    get(
        applicationId: string,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<MilestoneApp[]>> {
        return client.get<MilestoneApp[]>(`/application/${applicationId}/milestones`, customConfig);
    },
    post(
        applicationId: string | number,
        payload: MilestonePayload[],
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return client.post(`/application/${applicationId}/milestones`, payload, customConfig);
    },
    put(
        appId: string,
        milestones: MilestonePayload[],
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return client.put(`/application/${appId}/milestones`, milestones, customConfig);
    },
};

export const milestone = {
    post(
        applicationId: string | number,
        payload: MilestonePayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<MilestoneApp>> {
        return client.post(`/application/${applicationId}/milestone`, payload, customConfig);
    },

    put(
        applicationId: string,
        payload: MilestoneApp,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return client.put(`/application/${applicationId}/milestone/${payload.id}`, customConfig);
    },

    delete(
        applicationId: string,
        milestoneId: string,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return client.delete(
            `/application/${applicationId}/milestone/${milestoneId}`,
            customConfig
        );
    },
};

export function getMilestoneInvoice(appId: string, milestoneId: string) {
    return client.get(`/applications/${appId}/milestones/${milestoneId}/invoice`, {
        responseType: 'blob',
    });
}

export function editMilestonesPaymentStatus(appId: string, milestoneId: string, payload: string) {
    return client.put(`/applications/${appId}/milestones/${milestoneId}/payment-status`, {
        payment_status: payload,
    });
}

export function editMilestoneStatus(appId: string, milestoneId: string, payload: string) {
    return client.put(`/applications/${appId}/milestones/${milestoneId}/status`, {
        status: payload,
    });
}

export function sendInvoiceEmail(appId: string, milestoneId: string) {
    return client.get(`/applications/${appId}/milestones/${milestoneId}/email-invoice`);
}

export function editAccType(appId: string, milestoneId: string, payload: string) {
    return client.patch(`/applications/${appId}/milestones/${milestoneId}`, {
        account_type: payload,
    });
}

export function get(
    appId: string,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<Application>> {
    return client.get<Application>(`/application/${appId}`, customConfig);
}

export function post(
    payload: ApplicationPayload,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<Application>> {
    return client.post('application', payload, customConfig);
}

export function put(appId: string, payload: ApplicationPayload, customConfig?: AxiosRequestConfig) {
    return client.put(`/application/${appId}`, payload, customConfig);
}

export function patch(appId: string, payload: unknown, customConfig?: AxiosRequestConfig) {
    return client.patch(`/application/${appId}`, payload, customConfig);
}

export function createAcademicConsultation(
    // payload: ApplicationPayload,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<Application>> {
    return client.post('application_academic_consultation', customConfig);
}

export function acceptOffer(
    appId: string,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<Application>> {
    return client.get<Application>(`/application/accept_offer/${appId}`, customConfig);
}

export function rejectOffer(
    appId: string,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<Application>> {
    return client.get<Application>(`/application/reject_offer/${appId}`, customConfig);
}

export function setClientApplicationStatus(
    appId: string,
    payload: ClientApplicationStatusPayload,
    customConfig?: AxiosRequestConfig
) {
    return client.put(`/application/${appId}/client-status`, payload, customConfig);
}

export function recordReceivedVisa(
    userId: number,
    payload: ReceivedVisaPayload
): Promise<AxiosResponse<ReceivedVisaResponse>> {
    return client.post<ReceivedVisaResponse>(`/users/${userId}/received-visa`, payload);
}
